import { PortfolioDto } from "../../features/portfolio-info/types/PortfolioDto";
import { PortfolioModel } from "../../features/portfolio-info/types/PortfolioModel";
import { PortfolioItemDto } from "../../features/portfolio-info/types/PortfolioItemDto";
import { PortfolioItemModel } from "../../features/portfolio-info/types/PortfolioItemModel";
import { UnderlyingAssetDto } from "../../features/portfolio-info/types/UnderlyingAssetDto";
import { UnderlyingAssetModel } from "../../features/portfolio-info/types/UnderlyingAssetModel";
import { PortfolioSettingsDto } from "../../features/portfolio-info/types/PortfolioSettingsDto";
import { PortfolioSettingsModel } from "../../features/portfolio-info/types/PortfolioSettingsModel";
import { PortfolioBalanceHistoryEntryDto } from "../../features/portfolio-info/types/PortfolioBalanceHistoryEntryDto";
import {
    PortfolioBalanceHistoryEntryModel
} from "../../features/portfolio-info/types/PortfolioBalanceHistoryEntryModel";
import { assetService } from "../services/AssetService";
import {
    BlackScholesModelCalculationService,
    blackScholesModelCalculationService
} from "../services/BlackScholesModelCalculationService";
import { OptionInfoDto } from "../../features/options-table/types/OptionInfoDto";
import { AssetType } from "../types/AssetType";
import { PositionDto } from "../../features/portfolio-info/types/PositionDto";
import { PositionModel } from "../../features/portfolio-info/types/PositionModel";
import { AutoOrderConfigurationDto } from "../../features/portfolio-info/types/AutoOrderConfigurationDto";
import { AutoOrderConfigurationModel } from "../../features/portfolio-info/types/AutoOrderConfigurationModel";
import { AutoOrderState } from "../../features/portfolio-info/types/AutoOrderState";

class PortfolioMappers {
    map(dto: PortfolioDto, optionInfos: OptionInfoDto[], underlyingAssetPrice: number | null): PortfolioModel {
        return {
            id: dto.Id,
            name: dto.Name,
            description: dto.Description,
            marginBuffer: dto.MarginBuffer,
            inceptionDate: new Date(dto.InceptionDate),
            useValuationDate: dto.UseValuationDate,
            valuationDate: new Date(dto.ValuationDate),
            underlyingAsset: {
                id: dto.UnderlyingAsset.Id,
                name: dto.UnderlyingAsset.Name,
            },
            positions: dto.Positions !== null
                ? dto.Positions.map(x => this.mapPosition(x, optionInfos, underlyingAssetPrice))
                : [],
            portfolioItems: dto.PortfolioItems !== null
                ? dto.PortfolioItems.map(x => this.mapOrder(x, optionInfos, underlyingAssetPrice))
                : [],
            portfolioBalanceHistory: dto.PortfolioBalanceHistory !== null
                ? dto.PortfolioBalanceHistory.map(this.mapPortfolioBalanceHistoryEntry)
                : [],
            portfolioSettings: dto.PortfolioSettings !== null
                ? this.mapSettings(dto.PortfolioSettings)
                : null,
            modifiedAt: null,
        };
    }

    mapModelToModel(model: PortfolioModel): PortfolioModel {
        return {
            id: model.id,
            name: model.name,
            description: model.description,
            marginBuffer: model.marginBuffer,
            inceptionDate: new Date(model.inceptionDate),
            useValuationDate: model.useValuationDate,
            valuationDate: new Date(model.valuationDate),
            underlyingAsset: {
                id: model.underlyingAsset.id,
                name: model.underlyingAsset.name,
            },
            positions: model.positions !== null
                ? model.positions.map(this.mapPositionModelToModel)
                : [],
            portfolioItems: model.portfolioItems !== null
                ? model.portfolioItems.map(this.mapOrderModelToModel)
                : [],
            portfolioBalanceHistory: model.portfolioBalanceHistory !== null
                ? model.portfolioBalanceHistory.map(this.mapPortfolioBalanceHistoryEntryModelToModel)
                : [],
            portfolioSettings: model.portfolioSettings !== null
                ? this.mapSettingsModelToModel(model.portfolioSettings)
                : null,
            modifiedAt: null,
        };
    }
    
    mapUnderlyingAsset(assetDto: UnderlyingAssetDto): UnderlyingAssetModel {
        return {
            id: assetDto.Id,
            name: assetDto.Name,
        }
    }

    mapAutoOrderConfiguration(dto: AutoOrderConfigurationDto | null): AutoOrderConfigurationModel | null {
        if (dto === null) {
            return null;
        }

        return {
            id: dto.Id,
            portfolioItemId: dto.PortfolioItemId,
            strategy: dto.Strategy,
            state: dto.State ?? AutoOrderState.NotTriggered,
            stateDescription: dto.StateDescription ?? null,
            isActive: dto.IsActive,
            targetPrice: dto.TargetPrice,
            maxImpliedVolatilityLevel: dto.MaxImpliedVolatilityLevel,
            maxPriceDelta: dto.MaxPriceDelta,
            netDelta: dto.NetDelta,
        };
    }

    mapAutoOrderConfigurationToDto(model: AutoOrderConfigurationModel | null): AutoOrderConfigurationDto | null {
        if (model === null || model === undefined) {
            return null;
        }

        return {
            Id: model.id,
            PortfolioItemId: model.portfolioItemId,
            Strategy: model.strategy,
            IsActive: model.isActive,
            TargetPrice: model.targetPrice,
            MaxImpliedVolatilityLevel: model.maxImpliedVolatilityLevel,
            MaxPriceDelta: model.maxPriceDelta,
            NetDelta: model.netDelta,
        };
    }

    mapPositionModelToModel(positionDto: PositionModel): PositionModel {
        return {
            id: positionDto.id,
            portfolioId: positionDto.portfolioId,
            type: positionDto.type,
            symbol: positionDto.symbol,
            expireAt: positionDto.expireAt,
            strike: positionDto.strike,
            size: positionDto.size,
            volume: positionDto.volume,
            averageOpenPrice: positionDto.averageOpenPrice,
            expirationPrice: positionDto.expirationPrice,
            impliedVolatility: positionDto.impliedVolatility,
            customImpliedVolatility: positionDto.customImpliedVolatility,
            customImpliedVolatilityStep: positionDto.customImpliedVolatilityStep,
            markPrice: positionDto.markPrice,
            delta: positionDto.delta,
            gamma: positionDto.gamma,
            vega: positionDto.vega,
            theta: positionDto.theta,
        };
    }
    
    mapOrderModelToModel(itemModel: PortfolioItemModel): PortfolioItemModel {
        return new PortfolioItemModel({
            externalId: itemModel.externalId,
            exchangeOrderId: itemModel.exchangeOrderId,
            internalId: crypto.randomUUID(),
            autoOrderConfiguration: itemModel.autoOrderConfiguration,
            state: itemModel.state,
            isActive: itemModel.isActive,
            locked: itemModel.locked,
            openedPositionAt: itemModel.openedPositionAt,
            expireAt: itemModel.expireAt,
            impliedVolatilityAtOpen: itemModel.impliedVolatilityAtOpen,
            customImpliedVolatility: itemModel.customImpliedVolatility,
            customImpliedVolatilityStep: itemModel.customImpliedVolatilityStep,
            type: itemModel.type,
            strike: itemModel.strike,
            symbol: itemModel.symbol,
            size: itemModel.size,
            sizeStep: itemModel.sizeStep,
            openPrice: itemModel.openPrice,
            markPrice: itemModel.markPrice,
            markImpliedVolatility: itemModel.markImpliedVolatility,
            underlyingPriceAtOpen: itemModel.underlyingPriceAtOpen,
            delta: itemModel.delta,
            gamma: itemModel.gamma,
            vega: itemModel.vega,
            theta: itemModel.theta,
            comment: itemModel.comment,
            expirationPrice: itemModel.expirationPrice,
            dateForPricing: itemModel.dateForPricing,
            impliedVolatilityForPricing: itemModel.impliedVolatilityForPricing,
            underlyingPriceForPricing: itemModel.underlyingPriceForPricing,
        });
    }

    mapPortfolioBalanceHistoryEntry(itemDto: PortfolioBalanceHistoryEntryDto): PortfolioBalanceHistoryEntryModel {
        return {
            createdAt: itemDto.CreatedAt,
            currentBalance: itemDto.CurrentBalance,
            expirationBalance: itemDto.ExpirationBalance,
        };
    }

    mapPortfolioBalanceHistoryEntryModelToModel(itemModel: PortfolioBalanceHistoryEntryModel): PortfolioBalanceHistoryEntryModel {
        return {
            createdAt: itemModel.createdAt,
            currentBalance: itemModel.currentBalance,
            expirationBalance: itemModel.expirationBalance,
        };
    }
    
    getAssetParameters(
        assetType: AssetType,
        strike: number | null,
        expireAt: Date | null,
        impliedVolatility: number | null,
        riskFreeInterest: number | null,
        underlyingAssetPrice: number | null,
        currentDateTime: Date = new Date(),
    ): {
        price: number,
        delta: number,
        gamma: number | null,
        vega: number | null,
        theta: number | null,
        currentDateTime?: Date | null,
    } {
        let markPrice: number = 0;
        let delta: number = 0;
        let gamma: number | null = null;
        let vega: number | null = null;
        let theta: number | null = null;
    
        const isOption = assetService.isOptionType(assetType);
    
        if (!isOption) {
            markPrice = underlyingAssetPrice ?? 0;
            delta = 1;
        } else if (isOption && impliedVolatility && strike && expireAt && underlyingAssetPrice) {
            const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(expireAt, currentDateTime);
        
            const riskFreeInterestValue = riskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;
            
            markPrice = blackScholesModelCalculationService.getPremium(
                assetType, underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        
            delta = blackScholesModelCalculationService.getDelta(
                assetType, underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        
            gamma = blackScholesModelCalculationService.getGamma(
                underlyingAssetPrice, strike, timeToExpirationInShareOfYear, 
                impliedVolatility, riskFreeInterestValue);
        
            vega = blackScholesModelCalculationService.getVega(
                underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        
            theta = blackScholesModelCalculationService.getTheta(
                assetType, underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        }
    
        return { price: markPrice, delta, gamma, vega, theta };
    }

    mapPosition(
        positionDto: PositionDto,
        optionInfos: OptionInfoDto[],
        underlyingAssetPrice: number | null
    ): PositionModel {
        const optionInfoDto = optionInfos.find(x => x.Symbol === positionDto.Symbol) ?? null;
        const impliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? null;
        const riskFreeInterest = optionInfoDto?.RiskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;

        const { price, delta, gamma, vega, theta } =
            this.getAssetParameters(positionDto.Type, positionDto.Strike, positionDto.ExpireAt,
                impliedVolatility, riskFreeInterest, underlyingAssetPrice);

        return {
            id: positionDto.Id,
            portfolioId: positionDto.PortfolioId,
            type: positionDto.Type,
            symbol: positionDto.Symbol,
            expireAt: positionDto.ExpireAt,
            strike: positionDto.Strike,
            size: positionDto.Size,
            volume: positionDto.Volume,
            averageOpenPrice: positionDto.AverageOpenPrice,
            expirationPrice: positionDto.ExpirationPrice,
            impliedVolatility: impliedVolatility,
            customImpliedVolatility: positionDto.CustomImpliedVolatility,
            customImpliedVolatilityStep: positionDto.CustomImpliedVolatilityStep,
            markPrice: price,
            delta: delta * positionDto.Size,
            gamma: (gamma ?? 0) * positionDto.Size,
            vega: (vega ?? 0) * positionDto.Size,
            theta: (theta ?? 0) * positionDto.Size,
        };
    }
    
    mapOrder(
        itemDto: PortfolioItemDto,
        optionInfos: OptionInfoDto[],
        underlyingAssetPrice: number | null
    ): PortfolioItemModel {
        const optionInfoDto = optionInfos.find(x => x.Symbol === itemDto.Symbol) ?? null;
        const impliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? null;
        const riskFreeInterest = optionInfoDto?.RiskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;
        
        const { price, delta, gamma, vega, theta } =
            this.getAssetParameters(itemDto.Type, itemDto.Strike, itemDto.ExpireAt,
                impliedVolatility, riskFreeInterest, underlyingAssetPrice);
        
        return new PortfolioItemModel({
            externalId: itemDto.Id,
            exchangeOrderId: itemDto.ExchangeOrderId,
            internalId: crypto.randomUUID(),
            autoOrderConfiguration: this.mapAutoOrderConfiguration(itemDto.AutoOrderConfiguration),
            state: itemDto.State,
            isActive: itemDto.IsActive,
            locked: itemDto.Locked,
            openedPositionAt: itemDto.OpenedPositionAt,
            expireAt: itemDto.ExpireAt,
            impliedVolatilityAtOpen: itemDto.ImpliedVolatilityAtOpen,
            customImpliedVolatility: itemDto.CustomImpliedVolatility,
            customImpliedVolatilityStep: itemDto.CustomImpliedVolatilityStep ?? null,
            type: itemDto.Type,
            strike: itemDto.Strike,
            symbol: itemDto.Symbol,
            size: itemDto.Size,
            sizeStep: itemDto.SizeStep,
            openPrice: itemDto.OpenPrice,
            markPrice: price,
            markImpliedVolatility: itemDto.MarkImpliedVolatility,
            underlyingPriceAtOpen: itemDto.UnderlyingPriceAtOpen,
            delta: delta * itemDto.Size,
            gamma: (gamma ?? 0) * itemDto.Size,
            vega: (vega ?? 0) * itemDto.Size,
            theta: (theta ?? 0) * itemDto.Size,
            comment: itemDto.Comment,
            expirationPrice: itemDto.ExpirationPrice,
            dateForPricing: itemDto.DateForPricing,
            impliedVolatilityForPricing: itemDto.ImpliedVolatilityForPricing,
            underlyingPriceForPricing: itemDto.UnderlyingPriceForPricing,
        });
    }

    mapSettings(settingsDto: PortfolioSettingsDto | null): PortfolioSettingsModel | null {
        if (settingsDto === null) {
            return null;
        }
        
        return {
            useOffset: settingsDto.UseOffset,
            offset: settingsDto.Offset,
            minUnderlyingPrice: settingsDto.MinUnderlyingPrice,
            maxUnderlyingPrice: settingsDto.MaxUnderlyingPrice,
            useBalanceRangeRestriction: settingsDto.UseBalanceRangeRestriction,
            minBalance: settingsDto.MinBalance,
            maxBalance: settingsDto.MaxBalance,
            calculationStep: settingsDto.CalculationStep,
            selectedOptionParameterType: settingsDto.SelectedOptionParameterType,
            selectedTableInfoType: settingsDto.SelectedTableInfoType,
            selectedProfileTypes: settingsDto.SelectedProfileTypes
                ? [...settingsDto.SelectedProfileTypes]
                : [],
            selectedCustomDate: settingsDto.SelectedCustomDate,
            hideCommitedBalance: settingsDto.HideCommitedBalance,
            commitedBalance: settingsDto.CommitedBalance,
            showTradeSessionBalanceLine: settingsDto.ShowTradeSessionBalanceLine,
            tradeSessionBalance: settingsDto.TradeSessionBalance,
            showSupportLine: settingsDto.ShowSupportLine,
            supportLinePrice: settingsDto.SupportLinePrice,
            showResistanceLine: settingsDto.ShowResistanceLine,
            resistanceLinePrice: settingsDto.ResistanceLinePrice,
            useCustomPrice: settingsDto.UseCustomPrice,
            customPrice: settingsDto.CustomPrice,
            customPriceStep: settingsDto.CustomPriceStep,
            useComparedPortfolio: settingsDto.UseComparedPortfolio,
            comparedPortfolioId: settingsDto.ComparedPortfolioId,
            showDeltaNeutralPriceLines: settingsDto.ShowDeltaNeutralPriceLines,
            showPortfolioPerformance: settingsDto.ShowPortfolioPerformance,
            usePortfolioAnnualisedRoi: settingsDto.UsePortfolioAnnualisedRoi,
            showDeltaValues: settingsDto.ShowDeltaValues,
            balanceStartValueX: settingsDto.BalanceStartValueX,
            balanceEndValueX: settingsDto.BalanceEndValueX,
            balanceStartValueY: settingsDto.BalanceStartValueY,
            balanceEndValueY: settingsDto.BalanceEndValueY,
            isTripleCustomBalanceEnabled: settingsDto.IsTripleCustomBalanceEnabled,
            isTripleCustomBalanceUsingEqualShifts: settingsDto.IsTripleCustomBalanceUsingEqualShifts,
            isCustomImpliedVolatilityEnabled: settingsDto.IsCustomImpliedVolatilityEnabled,
            showClosedPositions: settingsDto.ShowClosedPositions,
            showLockedOrders: settingsDto.ShowLockedOrders,
        }
    }

    mapSettingsModelToModel(settingsModel: PortfolioSettingsModel): PortfolioSettingsModel {
        return {
            useOffset: settingsModel.useOffset,
            offset: settingsModel.offset,
            minUnderlyingPrice: settingsModel.minUnderlyingPrice,
            maxUnderlyingPrice: settingsModel.maxUnderlyingPrice,
            calculationStep: settingsModel.calculationStep,
            useBalanceRangeRestriction: settingsModel.useBalanceRangeRestriction,
            minBalance: settingsModel.minBalance,
            maxBalance: settingsModel.maxBalance,
            selectedOptionParameterType: settingsModel.selectedOptionParameterType,
            selectedTableInfoType: settingsModel.selectedTableInfoType,
            selectedProfileTypes: [...settingsModel.selectedProfileTypes],
            selectedCustomDate: settingsModel.selectedCustomDate,
            hideCommitedBalance: settingsModel.hideCommitedBalance,
            commitedBalance: settingsModel.commitedBalance,
            showTradeSessionBalanceLine: settingsModel.showTradeSessionBalanceLine,
            tradeSessionBalance: settingsModel.tradeSessionBalance,
            showSupportLine: settingsModel.showSupportLine,
            supportLinePrice: settingsModel.supportLinePrice,
            showResistanceLine: settingsModel.showResistanceLine,
            resistanceLinePrice: settingsModel.resistanceLinePrice,
            useCustomPrice: settingsModel.useCustomPrice,
            customPrice: settingsModel.customPrice,
            customPriceStep: settingsModel.customPriceStep,
            useComparedPortfolio: settingsModel.useComparedPortfolio,
            comparedPortfolioId: settingsModel.comparedPortfolioId,
            showDeltaNeutralPriceLines: settingsModel.showDeltaNeutralPriceLines,
            showPortfolioPerformance: settingsModel.showPortfolioPerformance,
            usePortfolioAnnualisedRoi: settingsModel.usePortfolioAnnualisedRoi,
            showDeltaValues: settingsModel.showDeltaValues,
            balanceStartValueX: settingsModel.balanceStartValueX,
            balanceEndValueX: settingsModel.balanceEndValueX,
            balanceStartValueY: settingsModel.balanceStartValueY,
            balanceEndValueY: settingsModel.balanceEndValueY,
            isTripleCustomBalanceEnabled: settingsModel.isTripleCustomBalanceEnabled,
            isTripleCustomBalanceUsingEqualShifts: settingsModel.isTripleCustomBalanceUsingEqualShifts,
            isCustomImpliedVolatilityEnabled: settingsModel.isCustomImpliedVolatilityEnabled,
            showClosedPositions: settingsModel.showClosedPositions,
            showLockedOrders: settingsModel.showLockedOrders,
        }
    }
    
    mapToDto(model: PortfolioModel): PortfolioDto {
        return {
            Id: model.id,
            Name: model.name,
            Description: model.description,
            MarginBuffer: model.marginBuffer,
            InceptionDate: model.inceptionDate,
            UseValuationDate: model.useValuationDate,
            ValuationDate: model.valuationDate,
            UnderlyingAsset: {
                Id: model.underlyingAsset.id,
                Name: model.underlyingAsset.name,
            },
            Positions: [],
            PortfolioItems: model.portfolioItems !== null
                ? model.portfolioItems.map(x => this.mapItemToDto(x))
                : [],
            PortfolioBalanceHistory: [],
            PortfolioSettings: model.portfolioSettings !== null
                ?   {
                        UseOffset: model.portfolioSettings.useOffset,
                        Offset: model.portfolioSettings.offset,
                        MinUnderlyingPrice: model.portfolioSettings.minUnderlyingPrice,
                        MaxUnderlyingPrice: model.portfolioSettings.maxUnderlyingPrice,
                        CalculationStep: model.portfolioSettings.calculationStep,
                        UseBalanceRangeRestriction: model.portfolioSettings.useBalanceRangeRestriction,
                        MinBalance: model.portfolioSettings.minBalance,
                        MaxBalance: model.portfolioSettings.maxBalance,
                        SelectedOptionParameterType: model.portfolioSettings.selectedOptionParameterType,
                        SelectedTableInfoType: model.portfolioSettings.selectedTableInfoType,
                        SelectedProfileTypes: [...model.portfolioSettings.selectedProfileTypes],
                        SelectedCustomDate: model.portfolioSettings.selectedCustomDate,
                        HideCommitedBalance: model.portfolioSettings.hideCommitedBalance,
                        CommitedBalance: model.portfolioSettings.commitedBalance,
                        ShowTradeSessionBalanceLine: model.portfolioSettings.showTradeSessionBalanceLine,
                        TradeSessionBalance: model.portfolioSettings.tradeSessionBalance,
                        ShowSupportLine: model.portfolioSettings.showSupportLine,
                        SupportLinePrice: model.portfolioSettings.supportLinePrice,
                        ShowResistanceLine: model.portfolioSettings.showResistanceLine,
                        ResistanceLinePrice: model.portfolioSettings.resistanceLinePrice,
                        UseCustomPrice: model.portfolioSettings.useCustomPrice,
                        CustomPrice: model.portfolioSettings.customPrice,
                        CustomPriceStep: model.portfolioSettings.customPriceStep,
                        UseComparedPortfolio: model.portfolioSettings.useComparedPortfolio,
                        ComparedPortfolioId: model.portfolioSettings.comparedPortfolioId,
                        ShowDeltaNeutralPriceLines: model.portfolioSettings.showDeltaNeutralPriceLines,
                        ShowPortfolioPerformance: model.portfolioSettings.showPortfolioPerformance,
                        UsePortfolioAnnualisedRoi: model.portfolioSettings.usePortfolioAnnualisedRoi,
                        ShowDeltaValues: model.portfolioSettings.showDeltaValues,
                        BalanceStartValueX: model.portfolioSettings.balanceStartValueX,
                        BalanceEndValueX: model.portfolioSettings.balanceEndValueX,
                        BalanceStartValueY: model.portfolioSettings.balanceStartValueY,
                        BalanceEndValueY: model.portfolioSettings.balanceEndValueY,
                        IsTripleCustomBalanceEnabled: model.portfolioSettings.isTripleCustomBalanceEnabled,
                        IsTripleCustomBalanceUsingEqualShifts: model.portfolioSettings.isTripleCustomBalanceUsingEqualShifts,
                        IsCustomImpliedVolatilityEnabled: model.portfolioSettings.isCustomImpliedVolatilityEnabled,
                        ShowClosedPositions: model.portfolioSettings.showClosedPositions,
                        ShowLockedOrders: model.portfolioSettings.showLockedOrders,
                    }
                :   null,
            CurrentBalance: 0,
            ExpirationBalance: 0,
        };
    }

    mapItemToDto(itemModel: PortfolioItemModel): PortfolioItemDto {
        return {
            Id: itemModel.externalId,
            ExchangeOrderId: itemModel.exchangeOrderId,
            AutoOrderConfiguration: this.mapAutoOrderConfigurationToDto(itemModel.autoOrderConfiguration),
            State: itemModel.state,
            IsActive: itemModel.isActive,
            Locked: itemModel.locked,
            OpenedPositionAt: itemModel.openedPositionAt,
            ExpireAt: itemModel.expireAt !== null
                ? itemModel.expireAt
                : null,
            ImpliedVolatilityAtOpen: itemModel.impliedVolatilityAtOpen,
            CustomImpliedVolatility: itemModel.customImpliedVolatility,
            Type: itemModel.type,
            Strike: itemModel.strike,
            Symbol: itemModel.symbol,
            Size: itemModel.size,
            SizeStep: itemModel.sizeStep,
            OpenPrice: itemModel.openPrice,
            MarkPrice: itemModel.markPrice,
            MarkImpliedVolatility: itemModel.markImpliedVolatility,
            UnderlyingPriceAtOpen: itemModel.underlyingPriceAtOpen,
            Delta: itemModel.delta,
            Gamma: itemModel.gamma,
            Vega: itemModel.vega,
            Theta: itemModel.theta,
            Comment: itemModel.comment,
            ExpirationPrice: itemModel.expirationPrice,
            DateForPricing: itemModel.dateForPricing,
            CustomImpliedVolatilityStep: itemModel.customImpliedVolatilityStep,
            ImpliedVolatilityForPricing: itemModel.impliedVolatilityForPricing,
            UnderlyingPriceForPricing: itemModel.underlyingPriceForPricing,
        };
    }
}

export const portfolioMappers = new PortfolioMappers();
